/**
 * InterpretMode v0.3.1
 *
 * 通訳モードの専用ビュー。currentMode === 'interpret' のとき
 * Camera/ScanLine の代わりに表示される。
 *
 * 内部構成（要件のレイアウト図参照）:
 *   1. 言語ペア表示 「🌐 LANG: EN → JA」（固定）
 *   2. StatusCard（4:5、状態カード）
 *   3. ORIGINAL ブロック（緑）— 確定原文 + バッファ中セグメント + interim
 *      v0.3.1+: バッファ中は右上に「○ buffering」微表記を出す
 *   4. JAPANESE ブロック（オレンジ）— 確定和訳
 *   5. HISTORY 直近3件
 *
 * START/STOP ボタン本体は親（App.tsx の actions 行）が担当する。
 */

import { StatusCard } from './StatusCard';

import type { InterpretStatus, ScanResult } from '../types';

import styles from './InterpretMode.module.css';

export interface InterpretModeProps {
  status: InterpretStatus;
  /** 翻訳が完了した確定原文（最新セグメント） */
  finalOriginal: string;
  /** 認識中の暫定原文（SR の interim、リアルタイム） */
  interimOriginal: string;
  /**
   * v0.3.1+: 確定済みだがまだ翻訳キューに乗っていないバッファセグメント。
   * isBuffering=true の間、ORIGINAL に finalOriginal の続きとして表示する。
   */
  pendingOriginal: string;
  /** バッファに何か入っているか（右上「○ buffering」表示） */
  isBuffering: boolean;
  /** 翻訳結果（最新セグメント） */
  japanese: string;
  /** 直近3件の履歴 */
  history: ScanResult[];
  errorMessage?: string | null;
}

/** ISO文字列を「HH:MM」形式に整形 */
function formatTime(iso: string): string {
  const date = new Date(iso);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function InterpretMode({
  status,
  finalOriginal,
  interimOriginal,
  pendingOriginal,
  isBuffering,
  japanese,
  history,
  errorMessage,
}: InterpretModeProps) {
  const showInterim = status === 'listening' && interimOriginal.length > 0;
  const showPending = pendingOriginal.length > 0;
  const showLiveLabel = status === 'listening' || isBuffering;

  // HISTORY は直近3件
  const top3 = history.slice(0, 3);

  return (
    <div className={styles.root}>
      <div className={styles.langPair}>
        <span className={styles.langPairIcon} aria-hidden>🌐</span>
        <span>LANG:</span>
        <span>EN</span>
        <span className={styles.langPairArrow}>→</span>
        <span>JA</span>
      </div>

      <StatusCard
        status={status}
        errorMessage={errorMessage ?? null}
      />

      {/* ORIGINAL ブロック */}
      <section className={`${styles.block} ${styles.blockOriginal}`}>
        <div className={styles.blockHeaderRow}>
          <span className={styles.blockHeader}>
            ▼ ORIGINAL{showLiveLabel ? ' (LIVE)' : ''}
          </span>
          {/* v0.3.1+: バッファ中は微表記で「○ buffering」 */}
          {isBuffering && (
            <span className={styles.bufferingBadge} aria-label="バッファ中">
              ○ buffering
            </span>
          )}
        </div>
        <p className={styles.blockBody}>
          {finalOriginal}
          {/* バッファ中セグメント（薄い緑文字、確定だが翻訳前） */}
          {showPending && (
            <>
              {finalOriginal && ' '}
              <span className={styles.pendingText}>{pendingOriginal}</span>
            </>
          )}
          {/* SR の暫定（さらに薄い斜体、未確定） */}
          {showInterim && (
            <>
              {(finalOriginal || showPending) && ' '}
              <span className={styles.interimText}>{interimOriginal}</span>
            </>
          )}
          {!finalOriginal && !showPending && !showInterim && ' '}
        </p>
      </section>

      {/* JAPANESE ブロック */}
      <section className={`${styles.block} ${styles.blockJapanese}`}>
        <span className={styles.blockHeader}>▼ JAPANESE</span>
        <p className={styles.blockBody}>{japanese || ' '}</p>
      </section>

      {/* HISTORY セクション */}
      {top3.length > 0 && (
        <section className={styles.history} aria-label="直近の通訳履歴">
          <h3 className={styles.historyTitle}>▼ HISTORY</h3>
          {top3.map((entry) => (
            <div key={entry.id} className={styles.historyItem}>
              <span className={styles.historyTime}>{formatTime(entry.timestamp)}</span>
              <p className={styles.historyText}>{entry.japanese ?? entry.text}</p>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
